import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  Definition,
  DefinitionErrorResponse,
  DataBuild,
  NewBuildRequest,
  JWTPayload,
  API_ENDPOINTS,
  STORAGE_KEYS,
  ENV_VARS,
  Environment,
  EnvironmentDetails,
  CreateEnvironmentRequest,
  UpdateEnvironmentRequest,
  Organization,
  DataSource,
  CreateDataSourceRequest
} from '../types';

// Get file extension for each language
const getFileExtensionForLanguage = (language: string): string => {
  const extensions: Record<string, string> = {
    "python": "py",
    "java": "java",
    "cpp": "cpp",
    "csharp": "cs",
    "go": "go",
    "js": "js",
    "php": "php",
    "ruby": "rb",
    "rust": "rs",
  };
  return extensions[language] || "txt";
};

// Helper function to download a blob as a file
const downloadBlob = (blob: Blob, filename: string): void => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

class NetworkManager {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    this.baseUrl = ENV_VARS.BACKEND_URL;
    this.token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  private decodeJWT(token: string): JWTPayload | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  public getOrganizationId(): string | null {
    const currentToken = this.token || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!currentToken) return null;
    const payload = this.decodeJWT(currentToken);
    return payload?.organizationID || null;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Always get the latest token from localStorage
    const currentToken = this.token || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    
    if (currentToken) {
      // Backend expects just the JWT token without "Bearer" prefix
      headers['Authorization'] = currentToken;
    }

    return headers;
  }

  public async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Handle FormData differently - don't override headers
    const isFormData = options.body instanceof FormData;
    const headers = isFormData ? {} : this.getHeaders();
    
    const config: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      // Try to parse the response as JSON first
      let responseData;
      let isJsonResponse = false;
      
      try {
        const textResponse = await response.text();
        // Try to parse as JSON
        try {
          responseData = JSON.parse(textResponse);
          isJsonResponse = true;
        } catch {
          // If JSON parsing fails, treat as plain text
          responseData = { message: textResponse };
          isJsonResponse = false;
        }
      } catch {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        // Handle 401 Unauthorized - token expired
        if (response.status === 401) {
          this.handleTokenExpiration();
          throw new Error('Session expired. Please log in again.');
        }
        
        // Handle 500 Internal Server Error
        if (response.status === 500) {
          console.log('500 Error - isJsonResponse:', isJsonResponse);
          console.log('500 Error - responseData:', responseData);
          // If the error has a buildId, serialize the entire error object to preserve it
          if (isJsonResponse && (responseData.buildId || (responseData.data && responseData.data.buildId))) {
            console.log('500 Error - Found buildId, serializing full response');
            throw new Error(JSON.stringify(responseData));
          }
          const errorMessage = isJsonResponse && responseData.message 
            ? responseData.message 
            : 'Internal server error. Please check if the backend server is running.';
          console.log('500 Error - Throwing error message:', errorMessage);
          throw new Error(errorMessage);
        }
        
        // For all JSON error responses, extract the message if available
        if (isJsonResponse) {
          // If the error has a buildId, include it in the error object
          if (responseData.buildId || (responseData.data && responseData.data.buildId)) {
            // Serialize the entire error object to preserve buildId
            throw new Error(JSON.stringify(responseData));
          }
          // If the error has an errors array (validation/semantic errors), serialize the entire object
          if (responseData.errors && Array.isArray(responseData.errors)) {
            // Serialize the entire error object to preserve errors array
            throw new Error(JSON.stringify(responseData));
          }
          const errorMessage = responseData.message || responseData.error || JSON.stringify(responseData);
          throw new Error(errorMessage);
        }
        
        // If it's a plain text response, use the text as the message
        if (!isJsonResponse && responseData.message) {
          throw new Error(responseData.message);
        }
        
        // Fallback error message
        throw new Error(responseData.message || `Network request failed with status ${response.status}`);
      }

      return responseData;
    } catch (error) {
      throw error;
    }
  }

  private handleTokenExpiration(): void {
    // Check if we've already handled token expiration to prevent multiple events
    if (this.token === null) {
      return; // Already handled
    }
    
    // Clear token and trigger logout
    this.logout();
    
    // Dispatch a custom event to notify the app about token expiration
    const event = new CustomEvent('tokenExpired', {
      detail: { message: 'Session expired. Please log in again.' }
    });
    window.dispatchEvent(event);
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.makeRequest<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    // Handle the actual backend response format
    const loginData = response.data as any;
    
    // Extract token from the correct field
    const token = loginData.accessToken || loginData.token || '';
    this.token = token;
    if (token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    }
    
    // Decode JWT to get user information
    const payload = this.decodeJWT(token);
    
    // Create the expected LoginResponse format
    const loginResponse: LoginResponse = {
      token: token,
      user: {
        id: payload?._id || loginData.user?.id || loginData.id || '1',
        email: payload?.email || loginData.user?.email || loginData.email || credentials.email,
        name: payload?.name || loginData.user?.name || loginData.name || 'User Profile',
        organizationID: payload?.organizationID || ''
      }
    };
    return loginResponse;
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Definitions API
  async getDefinitions(): Promise<Definition[]> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<Definition[]>(API_ENDPOINTS.DEFINITIONS.BASE(orgId));
    return response.data;
  }

  async getDefinition(id: string): Promise<Definition> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<Definition>(API_ENDPOINTS.DEFINITIONS.BY_ID(orgId, id));
    return response.data;
  }

  async createDefinition(formData: FormData): Promise<Definition> {
    console.log('=== CREATE DEFINITION METHOD ===');
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const currentToken = this.token || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!currentToken) throw new Error('No authentication token found');
    
    console.log('Organization ID:', orgId);
    console.log('Endpoint:', API_ENDPOINTS.DEFINITIONS.CREATE(orgId));
    console.log('FormData entries:');
    formData.forEach((value, key) => {
      console.log(`  ${key}:`, value);
    });
    
    try {
      const response = await this.makeRequest<Definition>(API_ENDPOINTS.DEFINITIONS.CREATE(orgId), {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: currentToken,
        },
      });
      console.log('Create definition success:', response);
      return response.data;
    } catch (error) {
      console.log('Create definition error caught:', error);
      throw error;
    }
  }

  async updateDefinition(id: string, definition: Partial<Definition>): Promise<Definition> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<Definition>(API_ENDPOINTS.DEFINITIONS.BY_ID(orgId, id), {
      method: 'PUT',
      body: JSON.stringify(definition),
    });
    return response.data;
  }

  async deleteDefinition(id: string): Promise<void> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    await this.makeRequest(API_ENDPOINTS.DEFINITIONS.BY_ID(orgId, id), {
      method: 'DELETE',
    });
  }

  async downloadProtoFile(id: string, title: string): Promise<void> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const currentToken = this.token || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!currentToken) throw new Error('No authentication token found');
    
    const url = `${this.baseUrl}${API_ENDPOINTS.DEFINITIONS.PROTO_FILE(orgId, id)}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': currentToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download proto file: ${response.statusText}`);
      }

      // Create filename from the definition title
      const sanitizedTitle = title.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_');
      const filename = `${sanitizedTitle}.proto`;

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw error;
    }
  }

  async getCompiledProtoClasses(id: string, language: string): Promise<void> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const currentToken = this.token || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!currentToken) throw new Error('No authentication token found');
    
    const url = `${this.baseUrl}/TLOG/definitions/${id}/compiled/${language}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': currentToken,
        },
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to get compiled classes: ${response.statusText}`);
        } catch {
          throw new Error(`Failed to get compiled classes: ${response.statusText}`);
        }
      }

      const contentType = response.headers.get('content-type');
      
      // Check if the response is source code (text content)
      if (contentType && contentType.includes('text/')) {
        const sourceCode = await response.text();
        const fileExtension = getFileExtensionForLanguage(language);
        const filename = `compiled_classes_${language}.${fileExtension}`;
        
        // Download as source code file
        const blob = new Blob([sourceCode], { type: 'text/plain' });
        downloadBlob(blob, filename);
        return;
      }

      // Download as ZIP file
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `compiled_classes_${language}.zip`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error(`No files were generated for ${language.toUpperCase()}. The downloaded file is empty.`);
      }
      
      downloadBlob(blob, filename);
    } catch (error) {
      throw error;
    }
  }

  // Gamedata API (renamed from Data Builds)
  async getDataBuilds(): Promise<DataBuild[]> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<DataBuild[]>(API_ENDPOINTS.GAMEDATA.BASE(orgId));
    return response.data;
  }

  async createDataBuild(build: NewBuildRequest): Promise<DataBuild> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<DataBuild>(API_ENDPOINTS.GAMEDATA.BASE(orgId), {
      method: 'POST',
      body: JSON.stringify(build),
    });
    
    // Handle case where response might not be wrapped in data property
    if (response.data) {
      return response.data;
    } else {
      // If response doesn't have data property, return the response itself
      return response as any;
    }
  }

  async getDataBuild(id: string): Promise<DataBuild> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<DataBuild>(API_ENDPOINTS.GAMEDATA.BY_ID(orgId, id));
    return response.data;
  }

  async deleteDataBuild(id: string): Promise<void> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    await this.makeRequest(API_ENDPOINTS.GAMEDATA.BY_ID(orgId, id), {
      method: 'DELETE',
    });
  }

  async decodeBundle(buildId: string, bundleName: string): Promise<any> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<any>(API_ENDPOINTS.GAMEDATA.DECODE(orgId, buildId, bundleName));
    return response.data;
  }

  // Environments API
  async getEnvironments(): Promise<Environment[]> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<Environment[]>(API_ENDPOINTS.ENVIRONMENTS.BASE(orgId));
    return response.data;
  }

  async createEnvironment(environment: CreateEnvironmentRequest): Promise<Environment> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<Environment>(API_ENDPOINTS.ENVIRONMENTS.CREATE(orgId), {
      method: 'POST',
      body: JSON.stringify(environment),
    });
    return response.data;
  }

  async updateEnvironment(envId: string, environment: UpdateEnvironmentRequest): Promise<Environment> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<Environment>(API_ENDPOINTS.ENVIRONMENTS.BY_ID(orgId, envId), {
      method: 'PUT',
      body: JSON.stringify(environment),
    });
    return response.data;
  }

  async getEnvironmentDetails(envName: string): Promise<EnvironmentDetails> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<EnvironmentDetails>(API_ENDPOINTS.ENVIRONMENTS.DETAILS(orgId, envName));
    return response.data;
  }

  async getEnvironmentDetailsById(envId: string): Promise<EnvironmentDetails> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<EnvironmentDetails>(API_ENDPOINTS.ENVIRONMENTS.BY_ID(orgId, envId));
    return response.data;
  }

  async updateEnvironmentConfigs(envId: string, configs: { deploymentPassword: string }): Promise<void> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    await this.makeRequest(API_ENDPOINTS.ENVIRONMENTS.CONFIGS(orgId, envId), {
      method: 'PUT',
      body: JSON.stringify(configs),
    });
  }

  // Organization API
  async getOrganizationInfo(): Promise<Organization> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<Organization>(API_ENDPOINTS.ORGANIZATION.INFO(orgId));
    return response.data;
  }

  async uploadCredentials(file: File): Promise<void> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const formData = new FormData();
    formData.append('credentials', file);
    
    // Get the current token for authorization
    const currentToken = this.token || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!currentToken) throw new Error('No authentication token found');
    
    await this.makeRequest(API_ENDPOINTS.ORGANIZATION.UPLOAD_CREDENTIALS(orgId), {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': currentToken,
      },
    });
  }

  async updateOrganizationConfig(config: { BuildJSON: boolean }): Promise<void> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    await this.makeRequest(API_ENDPOINTS.ORGANIZATION.CONFIG(orgId), {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Data Sources API
  async getDataSources(): Promise<DataSource[]> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<DataSource[]>(API_ENDPOINTS.ORGANIZATION.DATASOURCES(orgId));
    // Handle case where response might be directly an array or wrapped in data property
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  }

  async createDataSource(dataSource: CreateDataSourceRequest): Promise<DataSource> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    const response = await this.makeRequest<DataSource>(API_ENDPOINTS.ORGANIZATION.DATASOURCE(orgId), {
      method: 'POST',
      body: JSON.stringify(dataSource),
    });
    return response.data;
  }

  async deleteDataSource(datasourceId: string): Promise<void> {
    const orgId = this.getOrganizationId();
    if (!orgId) throw new Error('Organization ID not found');
    
    await this.makeRequest(API_ENDPOINTS.ORGANIZATION.DELETE_DATASOURCE(orgId, datasourceId), {
      method: 'DELETE',
    });
  }
}

export const networkManager = new NetworkManager();
export default networkManager;
